import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface Service {
  id: string;
  name: string;
  name_hindi?: string;
  description?: string;
  duration: number;
  price: number;
  category: string;
  image_url?: string;
}

const ServicesPreview = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedServices();
  }, []);

  const loadFeaturedServices = async () => {
    try {
      const servicesData = await apiClient.getServices();
      // Show only first 3 services as featured
      setServices(servicesData.slice(0, 3));
    } catch (error) {
      console.error('Failed to load services:', error);
      // Set empty services if API fails - admin should add services through admin panel
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'hair':
        return 'ri-scissors-line';
      case 'skin care':
        return 'ri-user-smile-line';
      case 'nails':
        return 'ri-hand-heart-line';
      case 'beauty':
        return 'ri-palette-line';
      default:
        return 'ri-service-line';
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-primary-400">Services</span>
            </h2>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-primary-400">Services</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Professional grooming services tailored to enhance your style and confidence
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-2xl shadow-luxury p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-primary-200 transition-colors">
                  <i className={`${getCategoryIcon(service.category)} text-2xl text-primary-400`}></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{service.name}</h3>
                  {service.name_hindi && (
                    <p className="text-sm text-primary-600 font-medium">{service.name_hindi}</p>
                  )}
                  <span className="text-primary-400 font-bold">From ₹{service.price}</span>
                </div>
              </div>

              {service.image_url && (
                <div className="w-full h-48 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl mb-6 overflow-hidden">

                  <img
                    src={resolveImageUrl(service.image_url)}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      // Hide image if it fails to load
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <p className="text-gray-600 mb-6 leading-relaxed">
                {service.description || `Professional ${service.name.toLowerCase()} service.`}
              </p>

              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-gray-500">Duration: {service.duration} min</span>
                <span className="text-sm text-gray-500">Category: {service.category}</span>
              </div>

              <Link
                to="/booking"
                state={{ selectedService: service }}
                className="block w-full bg-primary-400 hover:bg-primary-500 text-black py-3 rounded-xl font-bold text-center transition-all duration-300"
              >
                Book Now
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/services"
            className="inline-block bg-black hover:bg-gray-800 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;